import { useReducer } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  resendLoginCode,
  sendLoginMobileCode,
  verifyLoginCode,
} from "../../store/slices/userSlice";
import { loginMessages, loginRequests, loginSteps, pageRoutes } from "../../constants";
import signInReducer, { initialSignInState, signInActions } from "./signInReducer";
import AuthCard from "./AuthCard";
import AddMobileStep from "./AddMobileStep";
import VerifyCodeStep from "./VerifyCodeStep";

// The server sends seconds, the resend countdown runs to a timestamp
const resendTime = (seconds) => Date.now() + seconds * 1000;

// Details of the code the server just sent, as the verify step shows them
const sentCode = ({ maskedPhoneNumber, resendsRemaining, resendAvailableIn }) => ({
  maskedPhoneNumber,
  resendsRemaining,
  resendAt: resendTime(resendAvailableIn),
});

// A password step followed by the SMS code steps, used by every page that signs the user in (login,
// reset password). The page renders its own first step with renderFirstStep({ submit, isSubmitting, error }),
// where submit(thunk, payload) sends it and moves on to whichever code step the server asks for.
// onRestart(error) runs whenever the flow goes back to that first step.
export default function SignInFlow({ renderFirstStep, onRestart, initialError = "" }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [state, update] = useReducer(signInReducer, { ...initialSignInState, error: initialError });
  const { step, direction, fields, challenge, pendingRequest, error, notice } = state;

  const setField = (name) => (value) => update({ type: signInActions.FIELD_CHANGED, name, value });
  const changeNumber = () =>
    update({ type: signInActions.STEP_CHANGED, step: loginSteps.MOBILE_REQUIRED, direction: -1 });

  function restart(restartError = "") {
    update({ type: signInActions.RESTARTED, error: restartError });
    onRestart?.(restartError);
  }

  // Runs one sign-in request. Returns the response data, or null once the error is shown.
  async function request(requestType, thunk, payload) {
    update({ type: signInActions.REQUEST_STARTED, request: requestType });
    const result = await dispatch(thunk(payload));

    if (thunk.fulfilled.match(result)) return result.payload.data;

    const { message, restartLogin, resendAvailableIn } = result.payload || {};
    const requestError = typeof message === "string" ? message : loginMessages.GENERIC_ERROR;

    if (restartLogin) {
      restart(requestError);
    } else {
      update({
        type: signInActions.REQUEST_FAILED,
        error: requestError,
        resendAt: resendAvailableIn ? resendTime(resendAvailableIn) : null,
      });
    }

    return null;
  }

  async function submitFirstStep(thunk, payload) {
    const data = await request(loginRequests.SUBMIT, thunk, payload);
    if (!data) return;
    // Sessions without a second factor (server not yet on 2FA) are stored by the reducer
    if (data.token) return navigate(pageRoutes.APPLICATIONS);

    update({
      type: signInActions.CHALLENGE_UPDATED,
      step: data.step,
      challenge: {
        loginToken: data.loginToken,
        requiresPhoneNumber: data.step === loginSteps.MOBILE_REQUIRED,
        ...(data.step === loginSteps.OTP_REQUIRED && sentCode(data)),
      },
    });
  }

  async function handleSendMobileCode() {
    const data = await request(loginRequests.SUBMIT, sendLoginMobileCode, {
      loginToken: challenge.loginToken,
      phoneNumber: fields.phoneNumber,
    });
    if (!data) return;

    update({
      type: signInActions.CHALLENGE_UPDATED,
      step: loginSteps.OTP_REQUIRED,
      challenge: sentCode(data),
    });
  }

  async function handleResend() {
    const data = await request(loginRequests.RESEND, resendLoginCode, {
      loginToken: challenge.loginToken,
    });
    if (!data) return;

    update({
      type: signInActions.CHALLENGE_UPDATED,
      challenge: sentCode(data),
      notice: loginMessages.CODE_RESENT,
    });
  }

  async function handleVerify(code) {
    const data = await request(loginRequests.SUBMIT, verifyLoginCode, {
      loginToken: challenge.loginToken,
      code,
    });
    if (data) navigate(pageRoutes.APPLICATIONS);
  }

  function renderStep() {
    const sharedProps = { isSubmitting: pendingRequest === loginRequests.SUBMIT, error };
    // Wrapped so the click event isn't passed on as the restart error
    const goBack = () => restart();

    switch (step) {
      case loginSteps.MOBILE_REQUIRED:
        return (
          <AddMobileStep
            {...sharedProps}
            phoneNumber={fields.phoneNumber}
            onPhoneNumberChange={setField("phoneNumber")}
            onSubmit={handleSendMobileCode}
            onBack={goBack}
          />
        );

      case loginSteps.OTP_REQUIRED:
        return (
          <VerifyCodeStep
            {...sharedProps}
            code={fields.code}
            onCodeChange={setField("code")}
            maskedPhoneNumber={challenge.maskedPhoneNumber}
            resendAt={challenge.resendAt}
            resendsRemaining={challenge.resendsRemaining}
            canChangeNumber={challenge.requiresPhoneNumber}
            onVerify={handleVerify}
            onResend={handleResend}
            onChangeNumber={changeNumber}
            onBack={goBack}
            isResending={pendingRequest === loginRequests.RESEND}
            notice={notice}
          />
        );

      default:
        return renderFirstStep({ ...sharedProps, submit: submitFirstStep });
    }
  }

  return (
    <AuthCard step={step} direction={direction}>
      {renderStep()}
    </AuthCard>
  );
}
